import React, { useId, useMemo, useState } from "react"

import {
  ChevronDownIcon,
  cn,
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Tabs,
  TabsList,
  TabsTrigger,
} from "@coval/ui"

import { FieldLabel } from "@/components/shared/forms/FieldLabel"
import { MultiPhoneInboundVoiceFields } from "@/components/shared/forms/MultiPhoneInboundVoiceFields"
import { OutboundCallConfigFields } from "@/components/shared/forms/OutboundCallConfigFields"
import {
  InboundVoiceFields,
  LiveKitFields,
  PhoneNumberField,
  SMS_TOOLTIPS,
} from "@/components/shared/forms/SimulatorFormFields"
import { useMultiPhoneSupport } from "@/hooks/useMultiPhoneSupport"
import { AudioCodec, SimulatorType } from "@/types/api/SimulatorType"
import { LANGUAGE_TYPES } from "@/types/ModelConfigs/LanguageTypes"
import { PIPECAT_TOOLTIPS } from "@/utils/simulation/simulatorFields"

import {
  createLiveKitContentTypeUpdate,
  createLiveKitSandboxIdUpdate,
  getCurrentContentType,
  getCurrentSandboxId,
  getCustomHeaders,
  getCustomPayload,
} from "../utils/livekitHelpers"
import { createAgentPreviewMetadataView } from "./agent-preview-metadata-view"
import type { AgentTestConnectionConfig } from "./AgentTestConnectionButton"
import {
  AGENT_TYPE_GROUPS,
  AGENT_TYPE_ICONS,
  AgentTypeModal,
} from "./AgentTypeModal"
import {
  createPhoneNumberIndexUpdate,
  createSimulatorStringUpdate,
  createSttProviderUpdate,
} from "./simulator-metadata-semantics"
import {
  ChatWebSocketSimulatorConfig,
  TextSimulatorConfig,
  WebSocketSimulatorConfig,
} from "./simulators"

interface AgentPreviewSectionProps {
  simulatorType: SimulatorType
  simulationMetadata: Record<string, unknown>
  onSimulatorTypeChange: (value: string) => void
  onMetadataUpdate: (metadataUpdate: Record<string, unknown>) => void
  renderRequiredKeys: () => React.ReactNode
  testConnection?: AgentTestConnectionConfig | null
}

export function AgentPreviewSection({
  simulatorType,
  simulationMetadata,
  onSimulatorTypeChange,
  onMetadataUpdate,
  renderRequiredKeys,
  testConnection,
}: AgentPreviewSectionProps) {
  const agentTypeSelectId = useId()
  const [typeModalOpen, setTypeModalOpen] = useState(false)
  const { hasMultiPhoneSupport } = useMultiPhoneSupport()
  const metadataView = createAgentPreviewMetadataView(simulationMetadata)

  const isChatType =
    simulatorType === SimulatorType.TEXT ||
    simulatorType === SimulatorType.CHAT_WEBSOCKET

  // Map CHAT_WEBSOCKET to TEXT for the picker so both display as "Chat"
  const selectDisplayType =
    simulatorType === SimulatorType.CHAT_WEBSOCKET
      ? SimulatorType.TEXT
      : simulatorType

  const currentTypeOption = useMemo(
    () =>
      AGENT_TYPE_GROUPS.flatMap((group) => group.items).find(
        (item) => item.type === selectDisplayType,
      ),
    [selectDisplayType],
  )
  const CurrentTypeIcon = currentTypeOption
    ? AGENT_TYPE_ICONS[currentTypeOption.type]
    : undefined

  return (
    <div className="space-y-6">
      {/* Agent Type Selector */}
      <div className="space-y-2">
        <Label htmlFor={agentTypeSelectId}>
          Agent Type <span className="text-red-500">*</span>
        </Label>
        <button
          id={agentTypeSelectId}
          type="button"
          aria-label="Change agent type"
          onClick={() => setTypeModalOpen(true)}
          className={cn(
            "flex w-full items-center gap-3 rounded-lg border border-coval-border bg-surface-default p-3 text-left transition-colors hover:bg-surface-default-hover",
            "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-coval-info-border",
          )}
        >
          {CurrentTypeIcon && (
            <CurrentTypeIcon className="h-4 w-4 shrink-0 text-coval-text-muted" />
          )}
          <div className="min-w-0 flex-1">
            <div className="text-sm font-medium text-coval-text">
              {currentTypeOption?.label ?? "Choose an agent type"}
            </div>
            {currentTypeOption?.description && (
              <div className="mt-0.5 text-xs leading-relaxed text-coval-text-muted">
                {currentTypeOption.description}
              </div>
            )}
          </div>
          <ChevronDownIcon />
        </button>
        <AgentTypeModal
          isOpen={typeModalOpen}
          onClose={() => setTypeModalOpen(false)}
          onSelect={(type) => onSimulatorTypeChange(type)}
          groups={AGENT_TYPE_GROUPS}
          currentValue={selectDisplayType}
        />
      </div>

      {/* HTTP / WebSocket tab toggle for Chat agent type */}
      {isChatType && (
        <div className="space-y-2">
          <span className="text-sm text-coval-text-muted">Protocol</span>
          <Tabs value={simulatorType} onValueChange={onSimulatorTypeChange}>
            <TabsList
              variant="pill"
              aria-label="Chat protocol"
              className="w-full"
            >
              <TabsTrigger
                value={SimulatorType.TEXT}
                variant="pill"
                className="flex-1"
              >
                HTTP
              </TabsTrigger>
              <TabsTrigger
                value={SimulatorType.CHAT_WEBSOCKET}
                variant="pill"
                className="flex-1"
              >
                WebSocket
              </TabsTrigger>
            </TabsList>
          </Tabs>
        </div>
      )}

      {simulatorType === SimulatorType.VOICE &&
        (hasMultiPhoneSupport ? (
          <MultiPhoneInboundVoiceFields
            phoneNumber={metadataView.voice.phoneNumber}
            onPhoneNumberChange={(value) =>
              onMetadataUpdate({
                phone_number: value,
              })
            }
            phoneNumberIndex={Number(metadataView.voice.phoneNumberIndex) || 1}
            onPhoneNumberIndexChange={(value) =>
              onMetadataUpdate(createPhoneNumberIndexUpdate(value))
            }
            widebandEnabled={metadataView.voice.audioCodec === AudioCodec.L16}
            onWidebandChange={(enabled) =>
              onMetadataUpdate({
                audio_codec: enabled ? AudioCodec.L16 : AudioCodec.PCMU,
              })
            }
            customSipHeaders={metadataView.voice.customSipHeaders}
            onCustomSipHeadersChange={(value) =>
              onMetadataUpdate({ custom_sip_headers: value })
            }
            mediaEncryption={metadataView.voice.mediaEncryption}
            onMediaEncryptionChange={(value) =>
              onMetadataUpdate({ media_encryption: value })
            }
          />
        ) : (
          <InboundVoiceFields
            phoneNumber={metadataView.voice.phoneNumber}
            onPhoneNumberChange={(value) =>
              onMetadataUpdate({
                phone_number: value,
              })
            }
            customSipHeaders={metadataView.voice.customSipHeaders}
            onCustomSipHeadersChange={(value) =>
              onMetadataUpdate({ custom_sip_headers: value })
            }
            mediaEncryption={metadataView.voice.mediaEncryption}
            onMediaEncryptionChange={(value) =>
              onMetadataUpdate({ media_encryption: value })
            }
          />
        ))}

      {simulatorType === SimulatorType.SMS && (
        <PhoneNumberField
          value={metadataView.voice.phoneNumber}
          onChange={(value) =>
            onMetadataUpdate({
              phone_number: value,
            })
          }
          labelTooltip={SMS_TOOLTIPS.phoneNumber}
        />
      )}

      {/* Outbound Voice Simulator Fields */}
      {simulatorType === SimulatorType.OUTBOUND_VOICE && (
        <OutboundCallConfigFields
          config={metadataView.outboundCall}
          onConfigChange={(value) => onMetadataUpdate({ ...value })}
        />
      )}

      {/* PipeCat Simulator Fields */}
      {simulatorType === SimulatorType.PIPECAT && (
        <div className="space-y-4">
          <div className="space-y-2">
            <FieldLabel required tooltip={PIPECAT_TOOLTIPS.apiKey}>
              Pipecat Cloud API Key
            </FieldLabel>
            <Input
              value={metadataView.pipecat.apiKey}
              onChange={(e) =>
                onMetadataUpdate({
                  pipecat_api_key: e.target.value,
                })
              }
              aria-label="Pipecat Cloud API Key"
            />
          </div>
          <div className="space-y-2">
            <FieldLabel required tooltip={PIPECAT_TOOLTIPS.agentName}>
              Agent Name
            </FieldLabel>
            <Input
              value={metadataView.pipecat.agentName}
              onChange={(e) =>
                onMetadataUpdate({
                  agent_name: e.target.value,
                })
              }
              aria-label="Agent Name"
            />
          </div>
          <div className="space-y-2">
            <FieldLabel tooltip={PIPECAT_TOOLTIPS.customData}>
              JSON Custom Data (optional)
            </FieldLabel>
            <Input
              value={metadataView.pipecat.customData}
              onChange={(e) =>
                onMetadataUpdate({
                  custom_data: e.target.value,
                })
              }
              aria-label="JSON Custom Data"
            />
          </div>
        </div>
      )}

      {/* Required Keys for other simulator types */}
      {simulatorType !== SimulatorType.VOICE &&
        simulatorType !== SimulatorType.SMS &&
        simulatorType !== SimulatorType.NOOP &&
        simulatorType !== SimulatorType.TEXT &&
        simulatorType !== SimulatorType.CHAT_A2A &&
        simulatorType !== SimulatorType.WEBSOCKET &&
        simulatorType !== SimulatorType.CHAT_WEBSOCKET &&
        renderRequiredKeys()}

      {/* LiveKit Simulator Fields */}
      {simulatorType === SimulatorType.LIVEKIT && (
        <LiveKitFields
          tokenEndpoint={metadataView.livekit.tokenEndpoint}
          contentType={getCurrentContentType(simulationMetadata)}
          livekitUrl={metadataView.livekit.livekitUrl}
          agentName={metadataView.livekit.agentName}
          dispatchModeSelection={metadataView.livekit.dispatchMode}
          sandboxId={getCurrentSandboxId(simulationMetadata)}
          customHeaders={getCustomHeaders(simulationMetadata)}
          customPayload={getCustomPayload(simulationMetadata)}
          onTokenEndpointChange={(value) =>
            onMetadataUpdate({
              generate_token_endpoint: value,
            })
          }
          onContentTypeChange={(value) =>
            onMetadataUpdate(
              createLiveKitContentTypeUpdate(simulationMetadata, value),
            )
          }
          onLivekitUrlChange={(value) =>
            onMetadataUpdate({
              livekit_url: value,
            })
          }
          onAgentNameChange={(value) =>
            onMetadataUpdate({
              livekit_agent_name: value,
            })
          }
          onSandboxIdChange={(value) =>
            onMetadataUpdate(
              createLiveKitSandboxIdUpdate(simulationMetadata, value),
            )
          }
          onDispatchModeSelectionChange={(mode) =>
            onMetadataUpdate({
              livekit_dispatch_mode: mode,
            })
          }
          onCustomHeadersChange={(value) =>
            onMetadataUpdate({
              generate_token_headers: value,
            })
          }
          onCustomPayloadChange={(value) =>
            onMetadataUpdate({
              token_request_payload: value,
            })
          }
        />
      )}

      {/* STT Simulator Fields */}
      {simulatorType === SimulatorType.STT && (
        <div className="space-y-4">
          <div className="space-y-2">
            <Label>Provider</Label>
            <Select
              value={metadataView.stt.provider}
              onValueChange={(value) => {
                onMetadataUpdate(createSttProviderUpdate(value))
              }}
            >
              <SelectTrigger aria-label="Provider">
                <SelectValue placeholder="Select provider" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="openai">OpenAI</SelectItem>
                <SelectItem value="deepgram">Deepgram</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label>Model</Label>
            <Input
              placeholder="Enter model name"
              value={metadataView.stt.model}
              onChange={(e) =>
                onMetadataUpdate(
                  createSimulatorStringUpdate("model", e.target.value),
                )
              }
              disabled={metadataView.stt.provider === "openai"}
              aria-label="Model"
            />
          </div>
          <div className="space-y-2">
            <Label>Language</Label>
            <Select
              value={metadataView.stt.language}
              onValueChange={(value) => {
                onMetadataUpdate(createSimulatorStringUpdate("language", value))
              }}
            >
              <SelectTrigger aria-label="Language">
                <SelectValue placeholder="Select language" />
              </SelectTrigger>
              <SelectContent>
                {LANGUAGE_TYPES.map((language) => (
                  <SelectItem key={language.id} value={language.id}>
                    {language.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
      )}

      {simulatorType === SimulatorType.TEXT && (
        <>
          {renderRequiredKeys() || (
            <TextSimulatorConfig
              simulatorType={simulatorType}
              simulationMetadata={simulationMetadata}
              onMetadataUpdate={onMetadataUpdate}
              testConnection={testConnection}
            />
          )}
        </>
      )}

      {simulatorType === SimulatorType.CHAT_A2A && renderRequiredKeys()}

      {simulatorType === SimulatorType.WEBSOCKET && (
        <>
          {renderRequiredKeys() || (
            <WebSocketSimulatorConfig
              simulatorType={simulatorType}
              simulationMetadata={metadataView.websocket}
              onMetadataUpdate={onMetadataUpdate}
              testConnection={testConnection}
            />
          )}
        </>
      )}

      {simulatorType === SimulatorType.CHAT_WEBSOCKET && (
        <>
          {renderRequiredKeys() || (
            <ChatWebSocketSimulatorConfig
              simulatorType={simulatorType}
              simulationMetadata={metadataView.chatWebsocket}
              onMetadataUpdate={onMetadataUpdate}
              testConnection={testConnection}
            />
          )}
        </>
      )}
    </div>
  )
}
